import React from 'react'
import { Link } from 'react-router-dom'

import agent from 'agent'
import { AttackArguments } from 'components/attacks/list/AttackArguments'
import {
  getAttackConfiguration,
  getCommand,
  getTargetType
} from 'components/attacks/utils/attack-configuration'
import { AttackTypeIcon } from 'components/common/AttackTypeIcon'
import Button from 'components/common/Button'
import { DataTable } from 'components/common/DataTable'
import { DayPicker } from 'components/common/DayPicker'
import { EmptyState, EmptyStateTitle } from 'components/common/EmptyState'
import { EmptyStateMessage } from 'components/common/EmptyStateMessage'
import GlyphIcon from 'components/common/GlyphIcon'
import Pill from 'components/common/Pill'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import { getScheduleMaxRuns } from 'components/scenarios/utils'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import useMetadata from 'hooks/use-metadata'
import toast from 'lib/toast'
import theme from 'theme'
import { messageFromResponseError } from 'utils/strings'

import MaxRuns from './MaxRuns'
import TimePeriodForInvocation from './TimePeriodForInvocation'

import type { AttackSchedule } from 'types/api/schedules/active'

export const ScheduledAttackRow: React.FC<{
  schedule: AttackSchedule
  maxRuns?: number
  doFetch(): void
  isBasic?: boolean
  size?: 'base' | 'small'
}> = ({ schedule, maxRuns, doFetch, isBasic, size = 'base' }) => {
  const { currentTeamId } = useAuth()

  const attackConfig = getAttackConfiguration(schedule)

  const command = getCommand(attackConfig)
  const targetType = getTargetType(attackConfig)
  const { getAttackName } = useMetadata()

  return (
    <>
      <div
        css={{ marginLeft: theme.space[1] }}
        data-test-schedule-item={schedule.guid}
      >
        <div data-test-schedule-type>
          <GlyphIcon iconColor={theme.colors.green[0]}>
            <AttackTypeIcon icon={command.infraCommandType} />
          </GlyphIcon>
        </div>
      </div>
      <div>
        <Text
          data-test-schedule-label
          overflowEllipsis
          as={size === 'base' ? 'h4' : 'h5'}
        >
          {getAttackName(command.infraCommandType)}
        </Text>
        {Boolean(maxRuns) && <MaxRuns amount={maxRuns} />}
      </div>
      <TimePeriodForInvocation schedule={schedule} />
      {!isBasic && (
        <>
          <DayPicker isStatic activeDays={schedule.trigger.activeDays} />
          <div>
            {(schedule.target.type === 'Random'
              ? ['Random']
              : schedule.target.containers?.ids ??
                schedule.target.hosts?.ids ??
                []
            ).map((t) => (
              <Text key={t} overflowEllipsis as="small" color="medium">
                {t}
              </Text>
            ))}
            {targetType === 'Container' ? <Pill>Docker</Pill> : ''}
          </div>
          <AttackArguments commandConfig={command} />
          <div className="visible-on-row-hover">
            <Popover alignPopover={Popover.ALIGN_RIGHT}>
              {({ getTargetProps, getPopoverProps }) => (
                <>
                  <Icon
                    data-test-schedule-actions={schedule.guid}
                    icon="solidEllipsisVertical"
                    {...getTargetProps({
                      css: {
                        fontSize: theme.fontSizes[3],
                        color: theme.colors.steel[0],

                        '&:hover': {
                          color: theme.colors.steel[2]
                        }
                      }
                    })}
                  />
                  <PopoverMenu getPopoverProps={getPopoverProps}>
                    <TextButton
                      destructive
                      data-test-schedule-delete={schedule.guid}
                      onClick={async () => {
                        try {
                          await agent.delete(
                            `/schedules/${schedule.guid}?teamId=${currentTeamId}`
                          )
                          toast({
                            type: 'success',
                            text: 'Schedule deleted.'
                          })
                        } catch (err) {
                          toast({
                            type: 'error',
                            text: messageFromResponseError(
                              err,
                              'Unable to delete schedule. Please try again later.'
                            )
                          })
                        }
                        doFetch()
                      }}
                    >
                      Delete Scheduled Experiment
                    </TextButton>
                  </PopoverMenu>
                </>
              )}
            </Popover>
          </div>
        </>
      )}
    </>
  )
}

export const AttacksScheduleList: React.FC = () => {
  const { data, isLoadingError, isLoading, refetch } =
    useGremlinQuery<AttackSchedule[]>('/schedules/active')

  return (
    <DataTable
      columnTitles={['Name', false, 'Time', 'Days', 'Targets', 'Impact']}
      data={data}
      error={isLoadingError}
      gridColumns="64px minmax(48px, 1fr) minmax(48px, 1fr) minmax(256px, 1fr) minmax(128px, 1fr) minmax(128px, 1fr) 32px"
      loading={isLoading}
      renderEmpty={() => (
        <EmptyStateMessage
          data-test-attacks-list-empty
          icon="calendar"
          title="No Schedules Available"
        >
          <Link to="/attacks/new">Create an experiment</Link> to schedule.
        </EmptyStateMessage>
      )}
      renderError={(message) => (
        <EmptyState>
          <EmptyStateTitle>
            {message || 'There was an error loading schedules'}
          </EmptyStateTitle>
          <Button loading={isLoading} onClick={() => refetch()}>
            Retry
          </Button>
        </EmptyState>
      )}
      renderRow={(schedule) => {
        return (
          <ScheduledAttackRow
            doFetch={refetch}
            maxRuns={getScheduleMaxRuns(schedule)}
            schedule={schedule}
          />
        )
      }}
    />
  )
}
